<template>
  <div class="d-flex flex-column view-wrapper">
    
    <v-card class="elevation-1 rounded-0 mb-1 flex-shrink-0" color="primary">
      <v-card-text class="pa-2 d-flex justify-space-between align-center">
        <div class="text-subtitle-1 font-weight-bold white--text">
          Resumo de Vendas por Cliente
        </div>
        
        <v-chip small color="white" outlined>
          {{ items.length }} Registros
        </v-chip>
      </v-card-text>
    </v-card>

    <div class="content-container flex-grow-1 elevation-1 white" style="position: relative; min-height: 0;">
      
      <div v-if="loading" class="d-flex justify-center align-center fill-height">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="items.length === 0" class="d-flex justify-center align-center fill-height grey--text">
        <div class="text-center">
          <v-icon size="48" color="grey lighten-2">mdi-file-document-outline</v-icon>
          <p class="mt-2">Nenhuma venda encontrada.</p>
        </div>
      </div>

      <simplebar
        v-else
        :auto-hide="false"
        class="table-scroll-container"
        style="height: 100%;"
      >
        <table class="table-custom">
          <colgroup>
            <col style="width: 60%;" />  <col style="width: 20%;" />   <col style="width: auto;" /> </colgroup>
          <thead class="table-header sticky-header">
            <tr>
              <th class="text-left">Cliente</th>
              <th class="text-center pr-3">Quantidade</th>
              <th class="text-right pr-3">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in items"
              :key="item.id"
              class="table-row"
              :class="{ 'linha-alternada': index % 2 !== 0 }"
            >
              <td class="text-left text-truncate" style="max-width: 180px;" :title="item.nomeCliente">
                {{ item.nomeCliente }}
              </td>

              <td class="text-center">
                {{ item.quantidade }}
              </td>
              
              <td class="text-right pr-3 font-weight-bold">
                {{ formatarValor(item.valor) }}
              </td>
            </tr>
          </tbody>
        </table>
      </simplebar>
    </div>

    <v-card class="elevation-4 rounded-0 flex-shrink-0 grey lighten-4">
      <v-card-text class="pa-2">
        <v-row no-gutters justify="space-between" align="center">
          <v-col cols="4" class="text-center">
            <div class="caption grey--text text--darken-2">Venda Merc.</div>
            <div class="font-weight-bold blue--text text--darken-2 body-2">
              {{ formatarValor(totalVendaMercadoria) }}
            </div>
          </v-col>
          
          <v-col cols="4" class="text-center border-left border-right">
            <div class="caption grey--text text--darken-2">Faturados</div>
            <div class="font-weight-bold orange--text text--darken-3 body-2">
              {{ formatarValor(totalAFaturar) }}
            </div>
          </v-col>
          
          <v-col cols="4" class="text-center">
            <div class="caption grey--text text--darken-2">Total Geral</div>
            <div class="font-weight-black green--text text--darken-3 body-2">
              {{ formatarValor(totalValor) }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

  </div>
</template>

<script>
import simplebar from 'simplebar-vue';
import 'simplebar-vue/dist/simplebar.min.css';
import db from '@/plugins/dexie';

export default {
  name: 'ViewResumoVendasCliente',

  components: {
    simplebar,
  },

  data() {
    return {
      loading: false,
      items: [],
      idVendedor: 0,
    };
  },

    computed: {
    totalValor() {
      return this.items.reduce((acc, item) => acc + item.valor, 0);
    },
    
    totalVendaMercadoria() {
      return this.items.reduce((acc, item) => acc + item.valorVenda, 0);
    },
    
    totalAFaturar() {
      return this.items.reduce((acc, item) => acc + item.valorAFaturar, 0);
    },
  },
  
  created() {
    this.carregarVendas();
  },

  methods: {
    formatarValor(valor) {
      if (!valor) return '0,00';
      return valor.toLocaleString('pt-BR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    },

    async carregarVendas() {
      this.loading = true;
      this.items = [];

      try {
        const parametro = await db.parametro.toCollection().first();
        
        const idVendedorLogado = parametro && parametro.idvendedorlogado 
          ? parseInt(parametro.idvendedorlogado, 10) 
          : 0;
          
        if (idVendedorLogado === 0) {
          console.warn("Nenhum vendedor logado encontrado na tabela de parâmetros.");
          this.loading = false;
          return; 
        }
        this.idVendedor = idVendedorLogado;

        let inicioPeriodo, fimPeriodo;
        const paramInicio = this.$route.query['created-from'];
        const paramFim = this.$route.query['created-before'];

        if (paramInicio && paramFim) {
          const [anoIni, mesIni, diaIni] = paramInicio.split('-');
          inicioPeriodo = new Date(anoIni, mesIni - 1, diaIni);

          const [anoFim, mesFim, diaFim] = paramFim.split('-');
          fimPeriodo = new Date(anoFim, mesFim - 1, diaFim, 23, 59, 59);
        } else {
          const agora = new Date();
          inicioPeriodo = new Date(agora.getFullYear(), agora.getMonth(), 1);
          fimPeriodo = new Date(agora.getFullYear(), agora.getMonth() + 1, 0, 23, 59, 59);
        }

        const dataInicioStr = inicioPeriodo.toISOString();
        const dataFimStr = fimPeriodo.toISOString();
        
        let collection = db.venda
          .where('data')
          .between(dataInicioStr, dataFimStr, true, true)
          .and(venda => venda.idvendedor === idVendedorLogado && venda.cancelado !== 'S' && venda.idempresa == 1);

        const vendasFiltradas = await collection.toArray();

        if (vendasFiltradas.length === 0) {
          this.loading = false;
          return;
        }

        const idsClientes = [...new Set(vendasFiltradas.map(v => v.idcliente))];
        const clientes = await db.cliente.where('idcliente').anyOf(idsClientes).toArray();
        const mapaClientes = {};
        clientes.forEach(c => {
            mapaClientes[c.idcliente] = c.nome || c.nomefantasia || '';
        });

        const vendasProcessadas = new Set();

        const agrupadoPorCliente = vendasFiltradas.reduce((acumulador, venda) => {
          const idCli = parseInt(venda.idcliente);
          const idVendaUnico = venda.idvenda;
          const natureza = venda.natureza ? venda.natureza.toUpperCase() : '';
          
          if (!acumulador[idCli]) {
            acumulador[idCli] = {
              idCliente: idCli,
              quantidade: 0,
              valorTotal: 0,
              valorVenda: 0,
              valorAFaturar: 0
            };
          }

          const chaveControle = `${idCli}_${idVendaUnico}`;

          if (!vendasProcessadas.has(chaveControle)) {
            const valorLiquido = parseFloat(venda.vlrliquidovenda) || 0;

            acumulador[idCli].quantidade += 1;
            acumulador[idCli].valorTotal += valorLiquido;

            if (natureza.includes('VENDA')) {
              acumulador[idCli].valorVenda += valorLiquido;
            } else {
              acumulador[idCli].valorAFaturar += valorLiquido;
            }

            vendasProcessadas.add(chaveControle);
          }

          return acumulador;
        }, {});

        this.items = Object.values(agrupadoPorCliente).map(grupo => {
          const nomeDoCliente = mapaClientes[grupo.idCliente] || `Cliente (${grupo.idCliente})`;

          return {
            id: grupo.idCliente,
            nomeCliente: nomeDoCliente,
            quantidade: grupo.quantidade,
            valor: grupo.valorTotal,
            valorVenda: grupo.valorVenda,
            valorAFaturar: grupo.valorAFaturar
          };
        });

        this.items.sort((a, b) => a.nomeCliente.localeCompare(b.nomeCliente));

      } catch (error) {
        console.error("Erro ao carregar vendas:", error);
      } finally {
        this.loading = false;
      }
    },

    formatarData(dataStr) {
      if (!dataStr) return '';
      try {
        if (dataStr.length >= 10) {
          const date = new Date(dataStr);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('pt-BR');
          }
        }
        return dataStr;
      } catch (e) {
        return dataStr;
      }
    }
  },

  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.carregarVendas();
      },
    },
  },
};
</script>

<style scoped>
.view-wrapper {
  height: calc(100vh - 100px);
  background-color: white;
}

.table-custom {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.table-header th {
  background-color: #f5f5f5;
  color: #616161;
  padding: 10px 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  border-bottom: 2px solid #e0e0e0;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: 0 2px 2px -1px rgba(0,0,0,0.1);
}

.table-row td {
  padding: 8px 4px;
  border-bottom: 1px solid #eeeeee;
  font-size: 0.85rem;
  color: #424242;
}

.linha-alternada {
  background-color: #fafafa;
}

.border-left {
  border-left: 1px solid #e0e0e0;
}
.border-right {
  border-right: 1px solid #e0e0e0;
}

.table-scroll-container ::v-deep .simplebar-scrollbar::before {
  background: rgba(76, 111, 175, 0.4) !important;
}
</style>